import * as Sentry from '@sentry/react'
import { useCallback } from 'react'
import { keccak256, zeroAddress } from 'viem'
import { namehash, normalize } from 'viem/ens'

import {
  CONTRACT_CONTROLLER_ADDRESS,
  CONTRACT_REGISTRAR_ADDRESS,
  CONTRACT_REGISTRY_ADDRESS,
  CONTRACT_RESOLVER_ADDRESS,
} from '@/constants/blockchain'
import ENS from '@/contracts/ENS'
import L1Controller from '@/contracts/L1NSController'
import L1NSRegistrar from '@/contracts/L1NSRegistrar'
import L1Resolver from '@/contracts/PublicResolver'
import { l1IdSubnetWagmiConfig } from '@/plugins/auth/config'
import { l1Node, NameInfo, oneBillionYears } from '@/utils/identity'

import { useChainFunctions } from './useChain'

const useL1NS = () => {
  const { readContract } = useChainFunctions(l1IdSubnetWagmiConfig)

  // Async functions

  // Check if handle is available
  const confirmHandleAvailability = useCallback(async (handle: string): Promise<boolean> => {
    try {
      const data = await readContract({
        address: CONTRACT_CONTROLLER_ADDRESS as `0x${string}`,
        abi: L1Controller,
        functionName: 'available',
        args: [handle],
      })
      return data as boolean
    } catch (error: any) {
      console.error('Error:', error)
      Sentry.withScope(scope => {
        scope.setTag('ignore_error', 'true')
      })
      throw new Error(`Error confirming L1NS handle availability: ${error.message}`)
    }
  }, [])

  // Get L1NS address from handle
  // First check if handle is available, and if it is, resolution doesn't proceed
  // If handle is taken, then proceeds with resolution
  const getL1nsAddress = useCallback(async (handle: string): Promise<string> => {
    try {
      // Check if handle is available first
      const available = await confirmHandleAvailability(handle)
      // If it is, then any resolution is not potentially invalid
      if (available) {
        return zeroAddress
      }
      // Resolve address from handle
      const name = normalize(`${handle}.l1`)
      const node = namehash(name)
      const data = await readContract({
        address: CONTRACT_RESOLVER_ADDRESS as `0x${string}`,
        abi: L1Resolver,
        functionName: 'addr',
        args: [node],
      })
      return data as string
    } catch (error: any) {
      console.error('Error:', error)
      Sentry.withScope(scope => {
        scope.setTag('ignore_error', 'true')
      })
      throw new Error(`Error getting L1NS Address from username ${handle}.\n
        Error Message: ${error.message}`)
    }
  }, [])

  // Get L1NS username from address
  // First, gets the handle from the reverse resolution
  // Then, checks if the forward resolution matches the address, which also checks
  // if the handle is available, leading to invalid resolution
  const getL1nsName = useCallback(async (address: string): Promise<string> => {
    try {
      const name = normalize(`${String(address).substring(2)}.addr.reverse`)
      const node = namehash(name)
      const data = await readContract({
        address: CONTRACT_RESOLVER_ADDRESS as `0x${string}`,
        abi: L1Resolver,
        functionName: 'name',
        args: [node],
      })
      const username = (data as string).split('.', 1)[0] ?? ''
      if (username === '') {
        return username
      }
      // Check forward resolution to see if address matches
      const forwardResolution = await getL1nsAddress(username)
      if (forwardResolution.toLowerCase() === address.toLowerCase()) {
        return username
      }
      // If forward resolution does not match, return empty string, as the username is not valid
      return ''
    } catch (error: any) {
      console.error('Error:', error)
      Sentry.withScope(scope => {
        scope.setTag('ignore_error', 'true')
      })
      throw new Error(`Error getting L1NS Username from address ${address}.\n
        Error Message: ${error.message}`)
    }
  }, [])

  const getL1nsNickname = useCallback(async (handle: string): Promise<string> => {
    const name = normalize(`${handle}.l1`)
    const node = namehash(name)
    try {
      const data = await readContract({
        address: CONTRACT_RESOLVER_ADDRESS as `0x${string}`,
        abi: L1Resolver,
        functionName: 'text',
        args: [node, 'displayname'],
      })
      return data as string
    } catch (error: any) {
      console.error('Error:', error)
      Sentry.withScope(scope => {
        scope.setTag('ignore_error', 'true')
      })
      throw new Error(`Error getting L1NS Nickname from username: ${error.message}`)
    }
  }, [])

  const getL1nsProfile = useCallback(async (address: string): Promise<string> => {
    const name = normalize(`${String(address).substring(2)}.addr.reverse`)
    const node = namehash(name)
    try {
      const data = await readContract({
        address: CONTRACT_RESOLVER_ADDRESS as `0x${string}`,
        abi: L1Resolver,
        functionName: 'text',
        args: [node, 'profile'],
      })
      return data as string
    } catch (error: any) {
      console.error('Error:', error)
      Sentry.withScope(scope => {
        scope.setTag('ignore_error', 'true')
      })
      throw new Error(`Error getting L1NS Profile from username: ${error.message}`)
    }
  }, [])

  const getL1nsNodeOwner = useCallback(async (node: string): Promise<string> => {
    try {
      const data = await readContract({
        address: CONTRACT_REGISTRY_ADDRESS as `0x${string}`,
        abi: ENS,
        functionName: 'owner',
        args: [node as `0x${string}`],
      })
      return data as string
    } catch (error: any) {
      console.error('Error:', error)
      Sentry.withScope(scope => {
        scope.setTag('ignore_error', 'true')
      })
      throw new Error(`Error getting L1NS Node Owner from node: ${error.message}`)
    }
  }, [])

  const getDifficulty = useCallback(async (): Promise<string> => {
    try {
      const data = await readContract({
        address: CONTRACT_CONTROLLER_ADDRESS as `0x${string}`,
        abi: L1Controller,
        functionName: 'difficulty',
      })
      return data as string
    } catch (error: any) {
      console.error('Error:', error)
      Sentry.withScope(scope => {
        scope.setTag('ignore_error', 'true')
      })
      throw new Error(`Error getting L1NS free username PoW difficulty: ${error.message}`)
    }
  }, [])

  const getNameInfoFromTokenId = useCallback(async (tokenId: string): Promise<NameInfo> => {
    // Compute node from tokenId
    const toHash = l1Node + BigInt(tokenId).toString(16).padStart(64, '0')
    const node = keccak256(toHash as `0x${string}`)
    // Get name from resolver
    const data = await readContract({
      address: CONTRACT_RESOLVER_ADDRESS as `0x${string}`,
      abi: L1Resolver,
      functionName: 'name',
      args: [node],
    })
    const name = (data as string).split('.', 1)[0] ?? ''
    // Get expiry from registrar
    const expiryData = await readContract({
      address: CONTRACT_REGISTRAR_ADDRESS as `0x${string}`,
      abi: L1NSRegistrar,
      functionName: 'nameExpires',
      args: [tokenId],
    })
    const expiry = expiryData as bigint
    const kind = expiry > oneBillionYears ? 'free' : 'premium'
    // Check if name is available, which means it has to be repurchased by user
    const availableData = await readContract({
      address: CONTRACT_REGISTRAR_ADDRESS as `0x${string}`,
      abi: L1NSRegistrar,
      functionName: 'available',
      args: [tokenId],
    })
    return { name, kind, expiry, available: availableData as boolean }
  }, [])

  return {
    getL1nsName,
    getL1nsAddress,
    getL1nsNickname,
    getL1nsProfile,
    getL1nsNodeOwner,
    confirmHandleAvailability,
    getDifficulty,
    getNameInfoFromTokenId,
  }
}

export default useL1NS
